import { createSlice } from '@reduxjs/toolkit'
// Replace Firebase imports with modular versions
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import {
  getDatabase,
  ref,
  set,
  update,
  push,
  onValue,
  off,
  get,
  query,
  orderByChild,
  equalTo
} from 'firebase/database';
import { getToken, isSupported } from 'firebase/messaging';
import Cookies from 'js-cookie';
import { detect } from 'detect-browser';
import { addDoctorLog } from 'utils/functions';
// Import the Firebase app instance
import { auth, database, messaging, } from 'utils/firebase';

// Function to get FCM token
const getFCMToken = async () => {
  let token = null;
  try {
    if (await isSupported()) {
      const newToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
      });
      token = newToken;
      console.log("token fb ", token);
    }
  } catch (error) {
    console.error('An error occurred while retrieving token:', error);
  }
  return token;
};

// ------------------------------------
// State
// ------------------------------------

const initialState = {
  checked: false,
  loggedIn: false,
  me: {},
  browserSessionID: null,
  browserInfo: null,
  doctorIPAddress: null

}

// ------------------------------------
// Slices
// -----------------------------------

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setMe: (state, action) => ({
      ...state,
      me: action.payload.me,
      loggedIn: action.payload.loggedIn,
      checked: true,
    }),
    setUserIP: (state, action) => {
      state.doctorIPAddress = action.payload;
    },
    setLoggedIn: (state, action) => ({
      ...state,
      loggedIn: action.payload,
    }),
    setBrowserSessionID: (state, action) => ({
      ...state,
      browserSessionID: action.payload,
    }),
    setBrowserInfo: (state, action) => ({
      ...state,
      browserInfo: action.payload,
    }),
  },
})

// ------------------------------------
// Actions
// -----------------------------------
// Utility functions for cookies

// Make sure the cookie never expires by setting a long expiration date (e.g., 20 years)
const setCookie = (cookieName, value) => {
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 20); // Set to 20 years in the future
  Cookies.set(cookieName, value, { expires: expirationDate });
};

// Retrieve the cookie value
const getCookie = (cookieName) => {
  return Cookies.get(cookieName);
};

// Utility function to generate a simple UUID (e.g., 8-character random alphanumeric string)
const generateSimpleUUID = () => {
  return Math.random().toString(36).substring(2, 10); // Generates a random 8-character string
};

// Fetch or generate the FCM token
const generateAndStoreToken = async (doctorId, browserSessionID) => {
  console.log("Store Token");

  const COOKIE_KEY = 'userUUID';
  let UUID = browserSessionID;
  if (!UUID) {
    UUID = getCookie(COOKIE_KEY);
  }
  // Generate a new UUID if not found in the cookie
  if (!UUID) {
    console.log("Cookie not found. Generating a new UUID.");
    UUID = generateSimpleUUID();
    setCookie(COOKIE_KEY, UUID); // Store the UUID in the cookie
  }

  // Get the browser's FCM token
  const browserToken = await getFCMToken(); // Using our redefined getFCMToken function
  console.log('Browser FCM Token:', browserToken);

  // If no token is retrieved, exit early
  if (!browserToken) {
    console.log("Failed to get the browser token.");
    return null;
  }

  let existingToken;
  // Check if the token already exists in Firebase for this UUID
  try {
    const tokenRef = ref(database, `/doctors/${doctorId}/notificationTokens/PHD/${UUID}`);
    const snapshot = await get(tokenRef);
    existingToken = snapshot.val();
    console.log('Existing Token in Firebase:', existingToken);
  }
  catch (error) {
    console.log("error fetching doctor token" + error);
  }

  // If an existing token is found and it matches the browser token, return early
  if (existingToken && existingToken === browserToken) {
    console.log('Tokens match. No update needed.');
    return existingToken;
  }
  // If no token exists or if tokens do not match, update Firebase with the new token
  else {
    console.log(existingToken ? 'Tokens do not match. Updating Firebase with the new token.' : 'No token found in Firebase. Storing the browser token.');
    try {
      const updates = {};
      updates[`/doctors/${doctorId}/notificationTokens/PHD/${UUID}`] = browserToken;
      const rootRef = ref(database);
      await update(rootRef, updates);
      console.log('FCM token successfully stored/updated in Firebase.');
      return browserToken; // Return the updated or new token
    } catch (error) {
      console.error('Error updating/storing FCM token in Firebase:', error);
      return null;
    }
  }
};

let userListener = null;
let newLogin = false;

const getUserBrowserSessionID = (dispatch, browserSessionID) => {

  if (!browserSessionID) {
    const COOKIE_KEY = 'userUUID';
    browserSessionID = getCookie(COOKIE_KEY);

    if (!browserSessionID) {
      browserSessionID = generateSimpleUUID();
      setCookie(COOKIE_KEY, browserSessionID);
    }
    dispatch(slice.actions.setBrowserSessionID(browserSessionID));
  }
  return browserSessionID;
}

const storeBrowserInfo = (dispatch) => {
  const browser = detect() || null;

  const connection = navigator.connection ? {
    effectiveType: navigator.connection.effectiveType,
    downlink: navigator.connection.downlink,
    rtt: navigator.connection.rtt
  } : null
  const browserInfo = {
    browser,
    connection
  };
  console.log("Browser Info", browserInfo);
  dispatch(slice.actions.setBrowserInfo(browserInfo));
}


export const authenticate = () => (dispatch, getState) => {
  let isLoggingOut = false; // Guard flag to prevent multiple logouts
  let { browserSessionID } = getState().app;  // Assuming getState() returns the whole Redux store
  browserSessionID = getUserBrowserSessionID(dispatch, browserSessionID);

  storeBrowserInfo(dispatch, browserSessionID);

  const cleanupListener = () => {
    if (userListener) {
      off(userListener);  // Unsubscribe using the off function
      userListener = null;
    }
  };

  onAuthStateChanged(auth, async (me) => {
    cleanupListener();

    if (!me) {
      return dispatch(
        slice.actions.setMe({
          loggedIn: false,
          checked: true,
          me: {},
        }),
      );
    }

    try {
      const idTokenResult = await me.getIdTokenResult();

      if (
        !(
          idTokenResult.claims.admin ||
          idTokenResult.claims.doctor ||
          idTokenResult.claims.counselor
        )
      ) {
        await signOut(auth);
        alert('You are not authorized!');
        newLogin = false;
        return dispatch(
          slice.actions.setMe({
            loggedIn: false,
            checked: true,
            me: {},
          }),
        );
      }

      // Get user from Firestore
      const userRef = ref(database, `doctors/${me.uid}`);
      userListener = userRef;  // Store reference to unsubscribe later

      onValue(userRef, async (user) => {
        const userData = user.exists() ? { id: me.uid, ...user.val() } : {};

        /* A deactivated account is signed out, on sign-in, on reload and the
           moment an admin deactivates it while this tab is open. */
        if (userData.profileStatus === 'deleted') {
          if (!isLoggingOut) {
            isLoggingOut = true;
            newLogin = false;
            await dispatch(logout()).catch((err) => console.error('Deactivated sign-out failed:', err));
            window.alert('This account has been deactivated.');
            isLoggingOut = false;
          }
          return;
        }

        if (newLogin && userData.role) {
          const onlineRef = ref(database, `doctors/${me.uid}/online`);
          await set(onlineRef, true);
          newLogin = false;
        }

        if (!isLoggingOut && userData?.online === false && userData?.role === 'Counselor') {
          // Use guard flag to prevent repeated logouts
          isLoggingOut = true;

          dispatch(logout());

          // Clear the flag after logout completes
          setTimeout(() => {
            isLoggingOut = false;
          }, 1000);

          return;
        }

        dispatch(
          slice.actions.setMe({
            loggedIn: user.exists(),
            me: user.exists() ? { id: me.uid, ...user.val() } : {},
            checked: true,
          }),
        );

        // Perform FCM token management after the user is authenticated
        await generateAndStoreToken(me.uid, browserSessionID); // Fetch or generate FCM token
      });

    } catch (error) {
      console.error('Error getting id token result:', error);
      dispatch(
        slice.actions.setMe({
          loggedIn: false,
          checked: true,
          me: {},
        }),
      );
    }
  });

  // Optional: Return a cleanup function if you need to call it manually later
  return cleanupListener;
};
export const setUserIP = (key) => (dispatch) => {
  try {
    dispatch(
      slice.actions.setUserIP(key || null),
    )
  } catch (e) {
    console.log(e)
  }
}


const signup =
  ({ fullName, email, password }) =>
    () =>
      new Promise(async (resolve, reject) => {
        try {
          // create user
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;

          // send confirmation email
          await sendEmailVerification(user);

          // store user info in firestore would use setDoc in Firestore v9
          // await setDoc(doc(firestore, 'users', user.uid), {
          //   fullName,
          //   email,
          // });

          resolve(user);
        } catch (err) {
          reject(err);
        }
      });

const login =
  ({ email, password }) =>
    (dispatch) =>
      new Promise(async (resolve, reject) => {
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;

          if (!user)
            reject(new Error('Failed to login. please try it again later'));
          // if (!user.emailVerified) await sendEmailVerification(user);
          addDoctorLog({
            action: 'LOGIN',
            category: 'session',
            doctorID: user.uid,
            description: 'Doctor logged in',
            details: {
              timestamp: Date.now(),
            },
          });
          newLogin = true;
          resolve(user);
        } catch (err) {
          reject(err);
        }
      });

const logout = (isAuto) => (dispatch) =>
  new Promise(async (resolve, reject) => {
    try {
      await signOut(auth);
      console.log("Logout calling in app Slice");
      dispatch(
        slice.actions.setMe({
          checked: true,
          loggedIn: false,
          me: {},
        }),
        dispatch(slice.actions.setUserIP(null)),
      );
      resolve();
    } catch (err) {
      console.log("Error in log out", err);

      reject(err);
    }
  });

const resetPassword = (email) => () => sendPasswordResetEmail(auth, email);

// ------------------------------------
// Exports
// ------------------------------------

export const actions = {
  ...slice.actions,
  authenticate,
  signup,
  login,
  logout,
  resetPassword,
  setUserIP
}

export default slice.reducer
